import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../components/ui/SectionHeading';
import SafeIcon from '../common/SafeIcon';
import { useFormSubmit } from '../hooks/useFormSubmit';

const SubmitCaseStudy = () => {
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const { status, error, submitForm, resetFormStatus } = useFormSubmit();

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const myForm = e.target;
    const formData = new FormData(myForm);
    const payload = Object.fromEntries(formData.entries());

    const file = formData.get('assets');
    if (file && file.size > 0) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert("Please ensure the file is under 5MB.");
        return;
      }
      try {
        const base64File = await toBase64(file);
        payload.assets = base64File;
        payload.assetsFileName = file.name;
        payload.assetMimeType = file.type;
      } catch (err) {
        console.error('File conversion failed', err);
      }
    } else {
      delete payload.assets; // Do not send empty file object
    }

    payload.listName = 'Case Study Submission';

    const success = await submitForm(payload);
    if (success) {
      myForm.reset();
      setFileName('');
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        
        <SectionHeading title="Submit Your Success Story." subtitle="Case Study Intake" align="center" className="dark:text-white" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-900 p-8 md:p-12 border border-slate-200 dark:border-slate-800 shadow-2xl rounded-3xl mt-12"
        >
          {status === 'success' ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-brand-cyan/20 text-brand-cyan rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon name="Award" className="w-12 h-12" />
              </div>
              <h3 className="text-3xl font-heading font-bold text-brand-dark dark:text-white mb-4">Submission Received</h3>
              <p className="text-slate-600 dark:text-slate-400 font-medium max-w-md mx-auto">
                Thank you for sharing your strategic victory. Our team will review the details and reach out regarding publication opportunities.
              </p>
              <button 
                onClick={resetFormStatus}
                className="mt-8 px-8 py-3 bg-brand-dark dark:bg-white text-white dark:text-brand-dark text-xs uppercase font-bold tracking-widest rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {status === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm font-medium">
                  {error || 'Failed to submit. Please try again.'}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">Client / Company Name</label>
                  <input name="clientName" required className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-4 focus:border-brand-cyan dark:focus:border-brand-cyan outline-none transition-colors rounded-xl font-medium dark:text-white" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">Project Title</label>
                  <input name="projectTitle" required className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-4 focus:border-brand-cyan outline-none transition-colors rounded-xl font-medium dark:text-white" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">The Challenge (Brief)</label>
                <textarea name="challenge" required rows="3" className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-4 focus:border-brand-cyan outline-none transition-colors rounded-xl font-medium resize-none dark:text-white"></textarea>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">Strategic Solution & Tech Stack Used</label>
                <textarea name="solution" required rows="3" className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-4 focus:border-brand-cyan outline-none transition-colors rounded-xl font-medium resize-none dark:text-white"></textarea>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">Measurable Outcomes (Metrics)</label>
                <input name="outcomes" required placeholder="e.g. 45% increase in lead velocity" className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-4 focus:border-brand-cyan outline-none transition-colors rounded-xl font-medium dark:text-white" />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-3">Upload Supporting Assets (Images/PDFs)</label>
                <div 
                  onClick={() => fileInputRef.current.click()}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-300 dark:border-slate-700 px-4 py-12 text-center cursor-pointer hover:border-brand-cyan dark:hover:border-brand-cyan transition-colors rounded-xl"
                >
                  <SafeIcon name="Upload" className="w-8 h-8 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-bold text-slate-600 dark:text-slate-300">{fileName || "Click to upload files"}</p>
                  <input name="assets" type="file" ref={fileInputRef} className="hidden" onChange={(e) => setFileName(e.target.files[0]?.name)} />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={status === 'loading'}
                className="w-full py-5 bg-brand-cyan text-brand-dark text-sm uppercase tracking-widest font-bold hover:bg-[#33b8aa] transition-colors rounded-xl disabled:opacity-50 shadow-xl shadow-brand-cyan/20"
              >
                {status === 'loading' ? 'Processing...' : 'Submit Case Study'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SubmitCaseStudy;